<template>
  <div class="typewriter-background">
    <!-- Login Required Message Overlay -->
    <transition name="fade">
      <div
        v-if="showLoginMessage"
        class="login-message-overlay"
        @click="dismissLoginMessage"
      >
        <div class="login-message-wrapper" @click.stop>
          <h3 class="serif bigText text-center">Login Required</h3>
          <div class="login-message-content">
            <p class="restricted-message">
              Please log in or register to proceed to the next content.
            </p>
            <button
              class="request-access-button"
              @click="openDrawerFromMessage"
            >
              Login to continue
            </button>
            <button class="dismiss-button" @click="dismissLoginMessage">
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- The main typewriter container -->
    <div class="typewriter-container" @click="handleContainerClick">
      <span
        v-for="(char, index) in displayedText"
        :key="index"
        class="typewriterText"
      >
        {{ char }}
      </span>
      <span v-if="isTyping" class="cursor">|</span>
    </div>

    <!-- Bottom area for either "Skip/Next" or "Login/Register" button -->
    <div class="bottom-section">
      <!-- If logged in, show Skip/Next button -->
      <button class="next-button" @click="advanceNow" aria-label="Next block">
        Next →
      </button>
      <button
        v-if="isLoggedIn"
        class="skip-button"
        @click="handleSkip"
        aria-label="Skip Intro or Next"
      >
        {{ typewriterFinished ? "Next" : "Skip Intro" }}
      </button>

      <!-- If NOT logged in, show button to open bottom drawer -->
      <button
        v-else
        class="skip-button"
        @click="openDrawer"
        aria-label="Login or Register"
      >
        Login to continue
      </button>
    </div>

    <!-- The bottom drawer with the AuthOverlay inside -->
    <BottomDrawer :isOpen="showDrawer" @closeDrawer="closeDrawer">
      <AuthOverlay class="compAuth" @authenticated="onAuthenticated" />
    </BottomDrawer>

    <!-- Copyright Footer -->
    <CopyrightFooter class="typewriter-footer" />
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, computed, watch } from "vue";
import config from "@/config";
import { useAuth } from "@/firebase/useAuth";

// Import your drawer & overlay
import BottomDrawer from "@/components/GenericComponents/BottomDrawer.vue";
import AuthOverlay from "@/components/GenericComponents/AuthOverlay.vue";
import CopyrightFooter from "@/components/GenericComponents/CopyrightFooter.vue";

export default {
  name: "TypewriterText",
  components: {
    BottomDrawer,
    AuthOverlay,
    CopyrightFooter,
  },
  props: {
    typewriterFinished: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["introFinished", "typingFinished", "skip"],
  setup(props, { emit }) {
    // === Auth Setup ===
    const { currentUser } = useAuth();
    const isLoggedIn = computed(
      () => !!(currentUser.value && currentUser.value.uid)
    );
    const showDrawer = ref(false);
    const showLoginMessage = ref(false);
    const waitingForAuth = ref(false);

    // Functions to show/dismiss login message
    function showLoginRequired() {
      showLoginMessage.value = true;
    }

    function dismissLoginMessage() {
      showLoginMessage.value = false;
    }

    function openDrawerFromMessage() {
      showLoginMessage.value = false;
      showDrawer.value = true;
    }

    // Functions to open/close drawer with guards
    function openDrawer() {
      if (showDrawer.value) {
        return;
      }
      showDrawer.value = true;
    }

    function closeDrawer() {
      showDrawer.value = false;
    }

    // Watch showDrawer to track who changes it
    watch(showDrawer, (newVal, oldVal) => {
      if (newVal !== oldVal) {
        console.trace("Stack trace:");
      }
    });

    // Called when AuthOverlay emits "authenticated"
    function onAuthenticated() {

      // close the drawer
      closeDrawer();
      // Set flag that we're waiting for Firebase auth state to update
      waitingForAuth.value = true;
    }

    // Watch for currentUser changes after authentication
    watch(currentUser, (newUser, oldUser) => {

      if (newUser && waitingForAuth.value) {
        // User just authenticated, clear the flag
        waitingForAuth.value = false;

        // If typing has finished, automatically proceed to next stage
        if (!isTyping.value) {
          emit("introFinished");
        } else {
        }
      }
    });

    // === Typewriter Logic ===
    const introText =
      config.introText ||
      "The spiral descent, subject to gravity, is the cosmic sequence of involution.^Sufficient density propels the spirit down, to already established matter, in order to make him participate in the evolution that is happening on the earthly realm.^Already on earth, the process of evolution can be associated with upwards spirals of light, the spirit of man being lifted towards his unlimited potential.| Satellite transmissions are enabling long-distant stories, forgotten realms & secret revelations.^In the larger context of all realities, a drop of water awakens, strong enough for its effect to ripple upon other drops, until the whole ocean roams through the distance.| We are inviting the public into a self-generated universe, showing you the involution journey of an individual soul through the direct lens of its creator, allowing the spectator to be a participant in the stories and dive deeper into its intricacies.^The Path is a personal universe made collective, a space where diversity becomes an expression of essential unity.^It is The Path's story but it's not only her story.";

    const sentences = ref([]);
    const displayedText = ref("");
    const currentSentenceIndex = ref(0);
    const currentWordIndex = ref(0);
    const currentCharIndex = ref(0);
    const isTyping = ref(true);
    const typingTimeout = ref(null);
    const finalPauseTimeout = ref(null);
    const autoAdvanceTimeout = ref(null);
    const canAdvance = ref(false);
    const pausedForMoment = ref(false);

    // Sentences that trigger a fade-out moment transition
    const momentTransitions = new Set([0, 1]);

    function parseSentences(text) {
      // First replace the ^ markers with actual newlines
      text = text.replace(/\^/g, "\n\n");

      return text
        .split("|")
        .map((sentence) => sentence.trim())
        .map((sentence) => sentence.split(" "));
    }

    function calculateTypingSpeed() {
      return 60; // faster typing per request
    }

    // After the 25s pause for a moment-transition block, fade out then continue
    function handleMomentTransition() {
      const el = document.querySelector(".typewriter-container");
      el?.classList.add("fade-out");
      setTimeout(() => {
        el?.classList.remove("fade-out");
        displayedText.value = "";
        // proceed to next sentence typing
        typeWriter();
      }, 3000); // matches CSS animation duration
    }

    // Longer auto-pause between blocks and manual advance
    const BLOCK_PAUSE_MS = 25000; // 25 seconds between blocks

    function advanceNow() {
      // If user clicks Next on the last block while not logged in, show login message
      if (
        isTyping.value &&
        currentSentenceIndex.value >= sentences.value.length - 1 &&
        !isLoggedIn.value
      ) {
        showLoginRequired();
        return;
      }
      // Allow skipping to the next block even while typing
      if (typingTimeout.value) {
        clearTimeout(typingTimeout.value);
        typingTimeout.value = null;
      }
      if (finalPauseTimeout.value) {
        clearTimeout(finalPauseTimeout.value);
        finalPauseTimeout.value = null;
      }
      if (autoAdvanceTimeout.value) {
        clearTimeout(autoAdvanceTimeout.value);
        autoAdvanceTimeout.value = null;
      }
      // If we're in the 25s pause, advance accordingly
      if (canAdvance.value) {
        canAdvance.value = false;
        if (pausedForMoment.value) {
          pausedForMoment.value = false;
          handleMomentTransition();
        } else {
          typeWriter();
        }
        return;
      }

      if (isTyping.value) {
        const isLastBlock =
          currentSentenceIndex.value >= sentences.value.length - 1;
        if (isLastBlock) {
          // On last block, behave like Skip
          handleSkip();
          return;
        }
        // Jump to next sentence/block
        currentSentenceIndex.value++;
        currentWordIndex.value = 0;
        currentCharIndex.value = 0;
        displayedText.value = "";
        typeWriter();
      } else {
        // Typing already finished: treat as Skip
        handleSkip();
      }
    }

    function onBlockFinished() {
      if (typingTimeout.value) {
        clearTimeout(typingTimeout.value);
        typingTimeout.value = null;
      }
      canAdvance.value = true;
      autoAdvanceTimeout.value = setTimeout(() => {
        if (canAdvance.value) {
          canAdvance.value = false;
          typeWriter();
        }
      }, BLOCK_PAUSE_MS);
    }

    // The main typing function
    function typeWriter() {
      const currentSentence = sentences.value[currentSentenceIndex.value];
      if (!currentSentence) {
        // No more sentences => typing finished
        isTyping.value = false;
        emit("typingFinished");

        // Wait 5s, then either proceed if logged in or block if not
        finalPauseTimeout.value = setTimeout(() => {
          if (!isLoggedIn.value) {
            showLoginRequired();
            // We do NOT emit "introFinished" if they're not logged in
          } else {
            // If logged in, emit introFinished so the next component can show
            emit("introFinished");
          }
        }, 10000); // Increased from 5s to 10s
        return;
      }

      // Continue typing the current sentence
      const currentWord = currentSentence[currentWordIndex.value];
      if (currentWordIndex.value < currentSentence.length) {
        if (currentCharIndex.value < currentWord.length) {
          displayedText.value += currentWord.charAt(currentCharIndex.value);
          currentCharIndex.value++;
          typingTimeout.value = setTimeout(typeWriter, calculateTypingSpeed());
        } else {
          // Finished current word, add space
          displayedText.value += " ";
          currentCharIndex.value = 0;
          currentWordIndex.value++;
          typingTimeout.value = setTimeout(typeWriter, 45);
        }
      } else {
        // Finished entire sentence
        displayedText.value += "\n\n";
        currentWordIndex.value = 0;
        const completedSentenceIndex = currentSentenceIndex.value;
        currentSentenceIndex.value++;

        // Check if we need a fade-out transition
        if (momentTransitions.has(completedSentenceIndex)) {
          // Keep text visible during the 25s pause, then fade and continue
          if (typingTimeout.value) {
            clearTimeout(typingTimeout.value);
            typingTimeout.value = null;
          }
          pausedForMoment.value = true;
          canAdvance.value = true;
          autoAdvanceTimeout.value = setTimeout(() => {
            if (canAdvance.value) {
              canAdvance.value = false;
              pausedForMoment.value = false;
              handleMomentTransition();
            }
          }, BLOCK_PAUSE_MS);
          return;
        }

        // Non-moment blocks: pause 25s with text visible, then continue typing
        onBlockFinished();
      }
    }

    // Handle click on main container to skip
    function handleContainerClick() {
      // Don't process clicks if drawer is open or user not logged in
      if (!isLoggedIn.value || showDrawer.value) return;

      // Clear any active timeouts
      if (typingTimeout.value) clearTimeout(typingTimeout.value);
      if (finalPauseTimeout.value) clearTimeout(finalPauseTimeout.value);
      if (autoAdvanceTimeout.value) clearTimeout(autoAdvanceTimeout.value);
      canAdvance.value = false;

      if (isTyping.value) {
        // Skip to next sentence if typing
        currentSentenceIndex.value++;
        currentWordIndex.value = 0;
        currentCharIndex.value = 0;
        displayedText.value = "";
      } else {
        // Skip the pause if typing is complete
        emit("introFinished");
      }
    }

    // If user tries to skip via button but is not logged in, show login message
    function handleSkip() {
      if (!isLoggedIn.value) {
        showLoginRequired();
        return;
      }
      emit("skip");
    }

    // Start typing on mount
    onMounted(() => {
      sentences.value = parseSentences(introText);
      typeWriter();
    });

    // Clear timeouts on unmount
    onBeforeUnmount(() => {
      if (typingTimeout.value) clearTimeout(typingTimeout.value);
      if (finalPauseTimeout.value) clearTimeout(finalPauseTimeout.value);
      if (autoAdvanceTimeout.value) clearTimeout(autoAdvanceTimeout.value);
    });

    return {
      displayedText,
      isTyping,
      handleSkip,
      canAdvance,
      advanceNow,
      // Drawer & Auth
      isLoggedIn,
      showDrawer,
      openDrawer,
      closeDrawer,
      onAuthenticated,
      typewriterFinished: computed(() => props.typewriterFinished),
      // Login message
      showLoginMessage,
      dismissLoginMessage,
      openDrawerFromMessage,
    };
  },
};
</script>

<style scoped>
/* 
  1) Keep the typewriter in the center
  2) Pin the skip/login button to the BOTTOM 
*/

/* Login Required Message Overlay */
.login-message-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
}

.login-message-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: var(--largeSpace);
  max-width: 500px;
  text-align: center;
}

.login-message-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--space);
  margin-top: var(--largeSpace);
}

.login-message-content .restricted-message {
  color: var(--text-white);
  text-align: center;
  font-size: var(--smallFontSize);
  font-style: italic;
  margin-bottom: var(--space);
}

.dismiss-button {
  background: transparent;
  border: 1px solid var(--grey);
  color: var(--grey);
  padding: 8px 20px;
  border-radius: var(--border-radius-pill);
  cursor: pointer;
  font-size: var(--smallFontSize);
  transition: all 0.3s ease;
  margin-top: var(--smallSpace);
}

.dismiss-button:hover {
  border-color: var(--text-white);
  color: var(--text-white);
}

/* Fade transition for overlay */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Main background covering the screen */
.typewriter-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  /* flex layout so container is centered, but we also want the button pinned at the bottom */
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* The scrolling text container */
.typewriter-container {
  font-family: "Courier New", Courier, monospace;
  font-size: var(--largeFontSize);
  color: var(--text-white);
  white-space: pre-wrap;
  overflow-y: scroll;
  position: absolute;
  top: var(--largeSpace);
  left: 50%;
  transform: translateX(-50%);
  line-height: 1.7rem;
  max-width: 550px;
  width: 100%;
  height: calc(100vh - (2 * var(--clickableHeight)) - (2 * var(--largeSpace)));
  padding: var(--largeSpace);
  opacity: 1;
}

/* 
   The bottom section is pinned to the bottom. 
   So the button is at the bottom center 
*/
.bottom-section {
  position: absolute;
  display: flex;

  bottom: var(--largeSpace);
  left: 50%;
  transform: translateX(-50%);
  bottom: var(--clickableHeight);
  flex-direction: column;
  align-items: center;
}

/* The skip button styling */
.skip-button {
  cursor: pointer;
  transition: background 0.3s ease, color 0.3s ease, filter 0.5s ease;
}

.skip-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Typing cursor animation */
.cursor {
  display: inline-block;
  animation: blink-caret 0.75s step-end infinite;
  color: var(--text-white);
  margin-left: 2px;
}

@keyframes blink-caret {
  from,
  to {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
}

/* Fade-out animation for moment transitions */
.fade-out {
  animation: fadeOut 3s forwards;
}
@keyframes fadeOut {
  to {
    opacity: 0;
  }
}

/* Mobile adjustments */
@media (max-width: 480px) {
  .typewriter-container {
    height: calc(90vh - (2 * var(--clickableHeight)) - (2 * var(--largeSpace)));
  }
}

.compAuth {
  display: flex;
  flex-direction: column;
  min-height: 80vh;
  justify-content: flex-end;
}

@media only screen and (min-width: 480px) {
  .compAuth {
    align-items: center;
    width: 100%;
  }
}

/* Discreet Next text-button (sits above the main bottom button) */
.next-row {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  /* Place just below the typewriter container */
  top: calc(
    var(--largeSpace) +
      (100dvh - (4 * var(--clickableHeight)) - (2 * var(--largeSpace)))
  );
  margin-top: 8px;
  display: flex;
  justify-content: center;
  pointer-events: auto;
  z-index: 3; /* ensure it's above the bottom Skip button */
}

.next-button {
  background: transparent;
  border: 0;
  color: var(--text-white, #fff);
  opacity: 0.65;
  font: inherit;
  letter-spacing: 0.02em;
  cursor: pointer;
  padding: 6px 10px;
}

.next-button:hover {
  opacity: 0.9;
}

body.light-theme .next-button {
  color: var(--text-light, #111);
}

/* Align Next button on small screens to match the 90vh container height */
@media (max-width: 480px) {
  .next-row {
    top: calc(
      var(--largeSpace) +
        (90vh - (2 * var(--clickableHeight)) - (2 * var(--largeSpace)))
    );
  }
}

/* Footer positioned at bottom of typewriter screen */
.typewriter-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
}
</style>
